import React, { useCallback, useContext, useEffect, useState } from "react";

import {
    alpha,
    Slider,
    SliderProps,
    styled,
    Typography,
    useTheme
} from "@mui/material";
import { Controller, FieldPath } from "react-hook-form";
import { calculateGradient } from "./utils/calculateGradient";
import { interpolateColor } from "./utils/interpolateColor";
import { generateSliderValueText } from "./utils/generateSliderValueText";
import { SearchFieldLabel } from "../components/label";
import { TemperatureUnitContext } from "../../../context/app-context";
import { convertToFahrenheit } from "../../../services/convertUnits";

const sstRange = [16, 30] as [number, number];
const tempRange = [0, 30] as [number, number];

type SearchSliderProps = {
    name: FieldPath<SearchFormInputs>;
    label: string;
    min?: number;
    max?: number;
    isFromSelectedValueUp?: boolean;
    isTemperature?: boolean;
    units?: "days" | "%" | "m/s" | "h";
    className?: string;
};

const MySlider = styled(Slider)<SliderProps>(({ theme }) => ({}));

export const TemperatureSlider = () => {};

export const SearchSlider = ({
    name,
    min,
    max,
    label,
    isFromSelectedValueUp,
    isTemperature,
    units,
    className
}: SearchSliderProps) => {
    const [selectedUnits] = useContext(TemperatureUnitContext);
    const sliderRef = React.useRef<HTMLDivElement>(null);
    const unitToShow = isTemperature ? ` °${selectedUnits}` : "";
    const convert = useCallback(
        (value: number) => {
            let convertedValue = value;
            if (selectedUnits === "F" && isTemperature) {
                convertedValue = Math.round(convertToFahrenheit(value));
            } else {
                convertedValue = value;
            }
            return `${convertedValue}${unitToShow}`;
        },
        [selectedUnits, isTemperature, unitToShow]
    );

    const minLabel = convert(min);
    const maxLabel = convert(max);
    const defaultValue = isTemperature
        ? [min, max]
        : isFromSelectedValueUp
        ? min
        : max;
    const theme = useTheme();
    const color = isFromSelectedValueUp
        ? theme.palette.primary.main
        : theme.palette.info.main;

    useEffect(() => {
        if (sliderRef.current) {
            sliderRef.current.addEventListener(
                "touchstart",
                (e) => {
                    const isThumb = (e.target as any)?.dataset?.index;

                    if (!isThumb) {
                        e.stopPropagation();
                    }
                },
                { capture: true }
            );
        }
    });

    return (
        <Controller<SearchFormInputs>
            name={name}
            defaultValue={defaultValue}
            render={({ field: { onChange, value } }) => {
                const [internalValue, setInternalValue] = useState<
                    number | number[]
                >(defaultValue);
                useEffect(() => {
                    setInternalValue(value);
                }, [value]);
                let gradient = null,
                    color1 = null,
                    color2 = null;
                if (isTemperature) {
                    const range = name == "weather.sst" ? sstRange : tempRange;
                    gradient = calculateGradient(
                        range,
                        internalValue as [number, number],
                        "C"
                    );
                    color1 = interpolateColor(
                        "#3db7fd",
                        "#e84749",
                        (internalValue[0] - range[0]) / (range[1] - range[0])
                    );
                    color2 = interpolateColor(
                        "#3db7fd",
                        "#e84749",
                        (internalValue[1] - range[0]) / (range[1] - range[0])
                    );
                }
                const trackColor = gradient
                    ? `linear-gradient(90deg, #3db7fd ${gradient[0]}%, #e84749 ${gradient[1]}%)`
                    : color;
                const railColor = "#ddd";
                const valueLabel =
                    generateSliderValueText({
                        value: internalValue,
                        unit: isTemperature ? selectedUnits : units,
                        measure: name
                    }) || "I don't care";

                return (
                    <div className={`${className} relative`}>
                        <SearchFieldLabel name={name} smallMargin>
                            {label}
                        </SearchFieldLabel>
                        <Typography className="relative top-[30px] h-[0px] w-full text-center text-[13px] ">
                            {valueLabel}
                        </Typography>
                        <Slider
                            ref={sliderRef}
                            marks={[
                                { value: min, label: minLabel },
                                { value: max, label: maxLabel }
                            ]}
                            color={
                                isFromSelectedValueUp || isTemperature
                                    ? "primary"
                                    : "info"
                            }
                            min={min}
                            max={max}
                            valueLabelFormat={(value) => convert(value)}
                            valueLabelDisplay="auto"
                            onChangeCommitted={(_, value) => onChange(value)}
                            onChange={(_, value) => setInternalValue(value)}
                            value={internalValue}
                            track={
                                isFromSelectedValueUp ? "inverted" : "normal"
                            }
                            sx={{
                                "&.MuiSlider-root": {
                                    width: "calc(100% - 24px)",
                                    marginLeft: "12px",
                                    marginBottom: "12px",
                                    touchAction: "auto"
                                },

                                "& .MuiSlider-track": {
                                    background: isFromSelectedValueUp
                                        ? railColor
                                        : trackColor,
                                    borderWidth: 0
                                },
                                "& .MuiSlider-rail": {
                                    background: isFromSelectedValueUp
                                        ? trackColor
                                        : railColor
                                },
                                "& .MuiSlider-mark": {
                                    display: "none"
                                },
                                "& .MuiSlider-markLabel": {
                                    color: (theme) =>
                                        theme.palette.shBlack.main,
                                    fontSize: 13,
                                    letterSpacing: 0.4
                                },
                                "& .MuiSlider-thumb": {
                                    backgroundColor: "white",
                                    borderWidth: 2,
                                    width: 18,
                                    height: 18,
                                    borderColor: color,
                                    "&[data-index='0']": {
                                        borderColor: color1,
                                        "&:hover, &.Mui-focusVisible, &.MuiActive":
                                            {
                                                boxShadow: `0px 0px 0px 8px ${alpha(
                                                    color1 || color,
                                                    0.16
                                                )}`
                                            }
                                    },
                                    "&[data-index='1']": {
                                        borderColor: color2,
                                        "&:hover, &.Mui-focusVisible, &.MuiActive":
                                            {
                                                boxShadow: `0px 0px 0px 8px ${alpha(
                                                    color2 || color,
                                                    0.16
                                                )}`
                                            }
                                    }
                                }
                            }}
                        />
                    </div>
                );
            }}
        />
    );
};
